// Copyright (c) 2023 Proto Labs, Inc. All Rights Reserved.

import { createApp } from 'vue';
import store from './store';
import { actionTypes as geoActions } from './store/geolocation';
import router from './router';
import i18n, { loadDefaultLanguageAsync } from './i18n';
import componentLibrary from '@protolabs/component-library';

import App from './App.vue';
import analytics from '@/utils/analytics';
import { isAbortError } from '@/utils/apiUtils';

// Startup data is only useful to this page, so stop requesting (and retrying)
// it as soon as the user navigates away.
const startupAbortController = new AbortController();
window.addEventListener('pagehide', () => startupAbortController.abort(), {
    once: true,
});

async function init() {
    const params = new URLSearchParams(document.location.search);
    const marketingLanguage = params.get('lang');
    const languageOverride = marketingLanguage || null;
    const signal = startupAbortController.signal;
    try {
        await Promise.all([
            store.dispatch(geoActions.LOAD_COUNTRIES, { signal }),
            store.dispatch(geoActions.LOAD_LINKED_GROUPS, { signal }),
            loadDefaultLanguageAsync(languageOverride),
        ]);
        analytics.identify();
    } catch (error) {
        if (!isAbortError(error)) {
            console.error(error);
        }
    }
}

init().then(() => {
    createApp(App)
        .use(i18n)
        .use(componentLibrary, {
            theme: 'protolabs-2026'
        })
        .use(store)
        .use(router)
        .mount('#app');
});
